import {testIdProps} from '@github-ui/test-id-props'
import {ArrowSwitchIcon, KebabHorizontalIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label, NavList, Stack} from '@primer/react'
import {useMemo} from 'react'

import type {MemexProjectColumnId} from '../../../api/columns/contracts/memex-column'
import {getColumnIcon} from '../../../components/column-detail-helpers'
import {NavLinkActionListItem} from '../../../components/react-router/action-list-nav-link-item'
import {isColumnUserEditable} from '../../../helpers/is-column-editable'
import {sortColumnsDeterministically} from '../../../helpers/util'
import {useColumnWarning} from '../../../hooks/use-column-warning'
import type {ColumnModel} from '../../../models/column-model'
import {useNavigate} from '../../../router'
import {useProjectRouteParams} from '../../../router/use-project-route-params'
import {PROJECT_SETTINGS_CUSTOM_FIELDS_ROUTE, PROJECT_SETTINGS_FIELD_ROUTE} from '../../../routes'
import {useIssueFieldsEnabled} from '../../../state-providers/issue-fields/use-issue-fields-enabled'
import {IssueFieldResources} from '../../../strings'
import styles from './column-list-nav.module.css'

type ColumnListProps = {
  columns: Array<ColumnModel>
  addNewAnchorRef: React.RefObject<HTMLButtonElement | null> | null
  /** Called when the "new field" action is selected */
  onNewField: () => void
  onFieldNavCallback?: (memexProjectColumnId: MemexProjectColumnId, event: React.MouseEvent) => void
}

export function ColumnListNav({columns: allColumns, addNewAnchorRef, onNewField, onFieldNavCallback}: ColumnListProps) {
  const issueFieldsEnabled = useIssueFieldsEnabled()

  const columns = useMemo(
    () => allColumns.filter(column => isColumnUserEditable(column)).sort(sortColumnsDeterministically),
    [allColumns],
  )

  return (
    <NavList.Group className={styles.columnListNav} {...testIdProps(`project-column-settings-list`)}>
      <NavList.GroupHeading as="h2" className={styles.groupHeading}>
        <Stack as="span" className={styles.headingGroup} direction="horizontal" align="center" justify="space-between">
          <Stack.Item as="span" grow>
            {issueFieldsEnabled ? <span>Fields</span> : <span>Custom fields</span>}
          </Stack.Item>
          <IconButton
            icon={PlusIcon}
            aria-label={issueFieldsEnabled ? 'Add field' : 'New field'}
            variant="invisible"
            ref={addNewAnchorRef}
            onClick={onNewField}
            {...testIdProps('add-new-field-button')}
          />
          <ColumnListOptionsMenu />
        </Stack>
      </NavList.GroupHeading>
      {useMemo(
        () =>
          columns.map(col => (
            <ColumnListItem
              key={col.id}
              column={col}
              onLinkClick={(e: React.MouseEvent) => {
                if (!onFieldNavCallback) return
                onFieldNavCallback(col.id, e)
              }}
            />
          )),
        [columns, onFieldNavCallback],
      )}
    </NavList.Group>
  )
}

type ColumnListItemProps = {
  column: ColumnModel
  onLinkClick: (e: React.MouseEvent) => void
}

function ColumnListItem({column, onLinkClick}: ColumnListItemProps) {
  const projectRouteParams = useProjectRouteParams()
  const Icon = getColumnIcon(column.dataType)
  const warningKind = useColumnWarning(column)
  const showColumnWarning = !!warningKind

  const warningMessage = showColumnWarning
    ? `${
        warningKind === 'column-name-collides-with-issue-field'
          ? IssueFieldResources.fieldNameCollisionTooltip
          : 'Action required'
      }`
    : column.name

  return (
    <NavLinkActionListItem
      to={{
        pathname: PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
          ...projectRouteParams,
          fieldId: column.id,
        }),
      }}
      onClick={onLinkClick}
      {...testIdProps(`ColumnSettingsItem{id: ${column.name}}`)}
    >
      {/* eslint-disable-next-line primer-react/direct-slot-children */}
      <NavList.LeadingVisual
        {...testIdProps(`ColumnSettingsItemIcon{id: ${column.name}}`)}
        className={styles.leadingVisual}
      >
        {/* eslint-disable-next-line react-hooks/static-components */}
        <Icon />
      </NavList.LeadingVisual>
      {column.name}
      {showColumnWarning && (
        // eslint-disable-next-line primer-react/direct-slot-children
        <NavList.TrailingVisual {...testIdProps(`ColumnSettingsItemBadge{id: ${column.name}}`)}>
          <Label aria-label={warningMessage} variant="attention">
            Action required
          </Label>
        </NavList.TrailingVisual>
      )}
    </NavLinkActionListItem>
  )
}

function ColumnListOptionsMenu() {
  const issueFieldsEnabled = useIssueFieldsEnabled()
  const projectRouteParams = useProjectRouteParams()
  const navigate = useNavigate()
  const onEditClick = () => {
    const url = PROJECT_SETTINGS_CUSTOM_FIELDS_ROUTE.generatePath(projectRouteParams)
    navigate(url)
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          aria-label={issueFieldsEnabled ? 'Reorder fields' : 'Custom field options'}
          variant="invisible"
          {...testIdProps('custom-field-options-button')}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={onEditClick} {...testIdProps('edit-custom-fields-action')}>
            <ActionList.LeadingVisual>
              <ArrowSwitchIcon />
            </ActionList.LeadingVisual>
            Reorder fields
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
